import {
  Box,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  MenuItem,
  TextField,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { athleteApi } from '../../api/athleteApi';
import { adminApi } from '../../api/adminApi';
import type { Athlete, CreateAthleteDto, Category } from '../../types/domain';
import { ATHLETE_AGE_CATEGORY_LABELS } from '../../types/domain';
import { AthleteDialog } from '../../components/athletes/AthleteDialog';
import { ConfirmDialog } from '../../components/ConfirmDialog';

interface Props {
  competitionId: number;
  categories: Category[];
}

interface FlatTeam {
  id: number;
  name: string;
  categoryName: string;
}

export function CompetitionAthletesTab({ competitionId, categories }: Props) {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const [filterTeamId, setFilterTeamId] = useState<number | 'all'>('all');
  const [editingAthlete, setEditingAthlete] = useState<Athlete | null>(null);
  const [createForTeamId, setCreateForTeamId] = useState<number | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<Athlete | null>(null);

  // Načti všechny týmy ze všech kategorií (paralelně)
  const teamsQueries = useQuery({
    queryKey: ['admin', 'competition', competitionId, 'all-teams'],
    queryFn: async () => {
      const all: FlatTeam[] = [];
      for (const cat of categories) {
        const teams = await adminApi.listTeams(cat.id);
        for (const t of teams) {
          all.push({ id: t.id, name: t.name, categoryName: cat.name });
        }
      }
      return all;
    },
    enabled: categories.length > 0,
  });

  const athletesQuery = useQuery({
    queryKey: ['competition', competitionId, 'athletes'],
    queryFn: () => athleteApi.listByCompetition(competitionId),
  });

  const createMut = useMutation({
    mutationFn: ({ teamId, dto }: { teamId: number; dto: CreateAthleteDto }) =>
      athleteApi.create(teamId, dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['competition', competitionId, 'athletes'] });
      enqueueSnackbar('Závodník vytvořen', { variant: 'success' });
      setDialogOpen(false);
    },
    onError: (e: any) =>
      enqueueSnackbar(e?.response?.data?.message ?? 'Chyba', { variant: 'error' }),
  });

  const updateMut = useMutation({
    mutationFn: ({ id, dto }: { id: number; dto: CreateAthleteDto }) =>
      athleteApi.update(id, dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['competition', competitionId, 'athletes'] });
      enqueueSnackbar('Závodník upraven', { variant: 'success' });
      setDialogOpen(false);
    },
    onError: (e: any) =>
      enqueueSnackbar(e?.response?.data?.message ?? 'Chyba', { variant: 'error' }),
  });

  const deleteMut = useMutation({
    mutationFn: (id: number) => athleteApi.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['competition', competitionId, 'athletes'] });
      enqueueSnackbar('Závodník smazán', { variant: 'success' });
      setConfirmDelete(null);
    },
    onError: (e: any) =>
      enqueueSnackbar(e?.response?.data?.message ?? 'Chyba', { variant: 'error' }),
  });

  const teams = teamsQueries.data ?? [];
  const athletes = (athletesQuery.data ?? []).filter((a) =>
    filterTeamId === 'all' ? true : a.teamId === filterTeamId,
  );

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        sx={{ mb: 2 }}
      >
        <Typography variant="h6" sx={{ flexShrink: 0 }}>
          Závodníci
        </Typography>
        <TextField
          select
          size="small"
          label="Družstvo"
          value={filterTeamId}
          onChange={(e) =>
            setFilterTeamId(e.target.value === 'all' ? 'all' : Number(e.target.value))
          }
          sx={{ minWidth: 240 }}
        >
          <MenuItem value="all">Všechna</MenuItem>
          {teams.map((t) => (
            <MenuItem key={t.id} value={t.id}>
              {t.categoryName} — {t.name}
            </MenuItem>
          ))}
        </TextField>
        <Box sx={{ flexGrow: 1 }} />
        <TextField
          select
          size="small"
          label="Přidat k družstvu"
          value=""
          onChange={(e) => {
            setCreateForTeamId(Number(e.target.value));
            setEditingAthlete(null);
            setDialogOpen(true);
          }}
          sx={{ minWidth: 240 }}
        >
          {teams.map((t) => (
            <MenuItem key={t.id} value={t.id}>
              {t.categoryName} — {t.name}
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      <Paper variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Jméno</TableCell>
              <TableCell>Družstvo</TableCell>
              <TableCell>Kategorie</TableCell>
              <TableCell>Ročník</TableCell>
              <TableCell>Reg. č.</TableCell>
              <TableCell align="right">Akce</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {athletes.map((a) => (
              <TableRow key={a.id} hover>
                <TableCell>
                  {a.lastName} {a.firstName}
                </TableCell>
                <TableCell>{a.teamName}</TableCell>
                <TableCell>{ATHLETE_AGE_CATEGORY_LABELS[a.ageCategory]}</TableCell>
                <TableCell>{a.birthYear ?? ''}</TableCell>
                <TableCell>{a.registrationNumber ?? ''}</TableCell>
                <TableCell align="right">
                  <IconButton
                    size="small"
                    onClick={() => {
                      setEditingAthlete(a);
                      setCreateForTeamId(a.teamId);
                      setDialogOpen(true);
                    }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => setConfirmDelete(a)}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {athletes.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={6}
                  align="center"
                  sx={{ color: 'text.secondary', py: 4 }}
                >
                  Žádní závodníci. Vyber družstvo v poli „Přidat k družstvu".
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      <AthleteDialog
        open={dialogOpen}
        athlete={editingAthlete}
        loading={createMut.isPending || updateMut.isPending}
        onCancel={() => setDialogOpen(false)}
        onSubmit={(dto) => {
          if (editingAthlete) {
            updateMut.mutate({ id: editingAthlete.id, dto });
          } else if (createForTeamId) {
            createMut.mutate({ teamId: createForTeamId, dto });
          }
        }}
      />

      <ConfirmDialog
        open={confirmDelete != null}
        title="Smazat závodníka?"
        message={`Smazat ${confirmDelete?.lastName} ${confirmDelete?.firstName}? Smaže i všechny jeho výsledky.`}
        destructive
        onCancel={() => setConfirmDelete(null)}
        onConfirm={() => confirmDelete && deleteMut.mutate(confirmDelete.id)}
      />
    </Box>
  );
}
